import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Typography,
  Space,
  Table,
  Select,
  Badge,
  Button,
  message,
  Grid,
  Alert,
  Popconfirm,
  Row,
  Col,
  Input,
  Radio,
  Tag,
} from 'antd';
import {
  UserOutlined,
  ExclamationCircleOutlined,
  MailOutlined,
  DeleteOutlined,
  SearchOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  remove_ga4_connectionApi,
  reauthenticate_ga4_connectionApi,
} from '../../api/admin.apis';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

export const ConnectionReAuthentication = ({ ga4Properties = [], onUpdated }) => {
  const screens = useBreakpoint();

  const [selectedProperties, setSelectedProperties] = useState({});
  const [loadingRemove, setLoadingRemove] = useState({});
  const [loadingReauth, setLoadingReauth] = useState({});
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('a'); // 'a' for all, 'b' for active/healthy, 'c' for issues/expired

  // ---------- Helper function (defined before use) ----------
  const getSelectedProperty = (record) => {
    return record.properties.find(
      p => p.id === selectedProperties[record.user.id]
    );
  };

  // ---------- Prepare Table Data ----------
  const groupedData = useMemo(() => {
    return ga4Properties.map(userData => {
      const { user, connections } = userData;

      const properties = connections.flatMap(connection =>
        connection.properties.map(property => ({
          id: property.id,
          name: property.name,
          connection_id: connection.connection_id,
          account_name: connection.account_name,
          status: connection.status, // active | expired | issue
          issue_reason: connection.issue_reason,
          selected: property.selected,
        }))
      );

      // Sort properties: selected first, then alphabetically
      properties.sort((a, b) => {
        if (a.selected && !b.selected) return -1;
        if (!a.selected && b.selected) return 1;
        return a.name.localeCompare(b.name);
      });

      const defaultPropertyId =
        user.selected_property_id ||
        properties.find(p => p.selected)?.id ||
        properties[0]?.id;

      return {
        user,
        properties,
        defaultPropertyId,
      };
    });
  }, [ga4Properties]);

  // ---------- Initialize Default Selected Properties ----------
  useEffect(() => {
    const initial = {};
    groupedData.forEach(item => {
      initial[item.user.id] = item.defaultPropertyId;
    });
    setSelectedProperties(initial);
  }, [groupedData]);

  // ---------- Filter Data ----------
  const filteredData = useMemo(() => {
    let filtered = groupedData;

    // Apply status filter
    if (filterStatus === 'b') {
      // Healthy/Active - users with active connection
      filtered = filtered.filter(record => {
        const property = getSelectedProperty(record);
        return property?.status === 'active';
      });
    } else if (filterStatus === 'c') {
      // Issues/Expired - users with non-active connection
      filtered = filtered.filter(record => {
        const property = getSelectedProperty(record);
        return property?.status !== 'active';
      });
    }

    // Apply search filter
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(record => {
        // Search in user name and email
        const userNameMatch = record.user.full_name.toLowerCase().includes(searchLower);
        const userEmailMatch = record.user.email.toLowerCase().includes(searchLower);
        
        // Search in property names and account names
        const propertyMatch = record.properties.some(property => 
          property.name.toLowerCase().includes(searchLower) ||
          (property.account_name && property.account_name.toLowerCase().includes(searchLower)) ||
          (property.issue_reason && property.issue_reason.toLowerCase().includes(searchLower))
        );

        return userNameMatch || userEmailMatch || propertyMatch;
      });
    }

    return filtered;
  }, [groupedData, searchText, filterStatus, selectedProperties]);

  // ---------- Calculate Counts ----------
  const healthyCount = groupedData.filter(record => {
    const property = getSelectedProperty(record);
    return property?.status === 'active';
  }).length;

  const issueCount = groupedData.filter(record => {
    const property = getSelectedProperty(record);
    return property?.status !== 'active';
  }).length;

  const isDataEmpty = !ga4Properties || ga4Properties.length === 0 || filteredData.length === 0;

  // ---------- Render Status ----------
  const renderStatus = status => {
    if (status === 'active') return <Badge status="success" text="Healthy" />;
    if (status === 'expired') return <Badge status="error" text="Expired" />;
    return <Badge status="warning" text="Issue" />;
  };

  // ---------- Filter Handlers ----------
  const handleStatusFilterChange = (e) => {
    const value = e.target.value;
    setFilterStatus(value);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
  };

  const clearFilters = () => {
    setSearchText('');
    setFilterStatus('a');
  };

  // ---------- Actions ----------
  const handleRemove = async (user, property) => {
    setLoadingRemove(prev => ({ ...prev, [user.id]: true }));
    try {
      const res = await remove_ga4_connectionApi(
        user.id,
        property.connection_id
      );

      if (res?.data?.status === 'success') {
        message.success('GA4 connection removed successfully');
        onUpdated?.();
      } else {
        message.error(res?.data?.message || 'Failed to remove connection');
      }
    } catch {
      message.error('Failed to remove connection');
    } finally {
      setLoadingRemove(prev => ({ ...prev, [user.id]: false }));
    }
  };

  const handleReAuthenticate = async user => {
    setLoadingReauth(prev => ({ ...prev, [user.id]: true }));
    try {
      const res = await reauthenticate_ga4_connectionApi(user.id);

      if (res?.data?.status === 'success') {
        message.success(
          `Re-authentication email sent to ${user.email}`
        );
      } else {
        message.error(res?.data?.message || 'Email sending failed');
      }
    } catch {
      message.error('Failed to send re-authentication email');
    } finally {
      setLoadingReauth(prev => ({ ...prev, [user.id]: false }));
    }
  };

  // ---------- Table Columns ----------
  const columns = [
    {
      title: 'USER',
      width: 200,
      render: (_, record) => (
        <Space align="start">
          <UserOutlined style={{ marginTop: 4 }} />
          <Space direction="vertical" size={4}>
            <Text strong>{record.user.full_name}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.user.email}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              <InfoCircleOutlined /> {record.properties.length} propert{record.properties.length === 1 ? 'y' : 'ies'}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'PROPERTY',
      width: 250,
      render: (_, record) => (
        <Select
          value={selectedProperties[record.user.id]}
          onChange={id =>
            setSelectedProperties(prev => ({
              ...prev,
              [record.user.id]: id,
            }))
          }
          style={{ width: '100%' }}
          dropdownMatchSelectWidth={false}
        >
          {record.properties.map(p => (
            <Select.Option key={p.id} value={p.id}>
              <Space direction="vertical" size={2}>
                <div>{p.name}</div>
                {p.account_name && (
                  <Text type="secondary" style={{ fontSize: 11 }}>{p.account_name}</Text>
                )}
              </Space>
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'STATUS',
      width: 120,
      render: (_, record) => {
        const property = getSelectedProperty(record);
        return renderStatus(property?.status);
      },
    },
    {
      title: 'ACTIONS',
      width: 200,
      render: (_, record) => {
        const property = getSelectedProperty(record);
        const needsReAuth = property?.status !== 'active';

        return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {needsReAuth && property?.issue_reason && (
              <Text type="danger" style={{ fontSize: 12 }}>
                <ExclamationCircleOutlined /> {property.issue_reason}
              </Text>
            )}

            <Space>
              <Popconfirm
                title="Re-authenticate GA4 connection?"
                description={
                  <>
                    <Text type="danger">
                      This action will delete all stored GA4 analytics data
                      for this property.
                    </Text>
                    <br />
                    <Text>
                      Data will be re-synced from GA4 once the user completes
                      authentication.
                    </Text>
                  </>
                }
                okText="Yes, Re-authenticate"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
                onConfirm={() => handleReAuthenticate(record.user)}
              >
                <Button
                  type="primary"
                  icon={<MailOutlined />}
                  size="small"
                  loading={loadingReauth[record.user.id]}
                  disabled={!needsReAuth}
                >
                  Re-Auth
                </Button>
              </Popconfirm>

              <Popconfirm
                title="Remove GA4 connection?"
                description="This will permanently remove the GA4 connection."
                okText="Remove"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
                onConfirm={() =>
                  handleRemove(record.user, property)
                }
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                  loading={loadingRemove[record.user.id]}
                >
                  Remove
                </Button>
              </Popconfirm>
            </Space>
          </Space>
        );
      },
    },
  ];

  // ---------- Render ----------
  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Header Row with Title, Search, and Radio */}
        <Row gutter={[16, 16]} align="middle">
          {/* Title Column */}
          <Col xs={24} sm={24} md={8}>
            <Space direction="vertical" size={0}>
              <Title level={5} style={{ margin: 0 }}>GA4 Connection Issues & Re-Authentication</Title>
              <Paragraph type="secondary" style={{ margin: 0 }}>
                Users: <strong>{groupedData.length}</strong> • Properties: <strong>{groupedData.reduce((acc, user) => acc + user.properties.length, 0)}</strong>
                {(searchText || filterStatus !== 'a') && (
                  <span> • Filtered: <strong>{filteredData.length}</strong></span>
                )}
              </Paragraph>
            </Space>
          </Col>

          {/* Search Column */}
          <Col xs={24} sm={24} md={8}>
            <Input
              placeholder="Search by user, email, property, or account..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={handleSearchChange}
              allowClear
              size={screens.xs ? 'small' : 'middle'}
            />
          </Col>

          {/* Radio Group Column */}
          <Col xs={24} sm={24} md={8}>
            <div style={{ textAlign: screens.md ? 'right' : 'left' }}>
              <Radio.Group 
                value={filterStatus} 
                onChange={handleStatusFilterChange}
                size={screens.xs ? 'small' : 'middle'}
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value="a">ALL ({groupedData.length})</Radio.Button>
                <Radio.Button value="b">HEALTHY ({healthyCount})</Radio.Button>
                <Radio.Button value="c">ISSUES ({issueCount})</Radio.Button>
              </Radio.Group>
            </div>
          </Col>
        </Row>

        {/* Active Filters Display */}
        {(searchText || filterStatus !== 'a') && (
          <Row>
            <Col span={24}>
              <Space size={8} wrap>
                <Text type="secondary" style={{ fontSize: 12 }}>Active filters:</Text>
                {filterStatus !== 'a' && (
                  <Tag 
                    closable 
                    onClose={() => setFilterStatus('a')}
                    style={{ marginRight: 0 }}
                  >
                    Status: {filterStatus === 'b' ? 'Healthy' : 'Issues'}
                  </Tag>
                )}
                {searchText && (
                  <Tag 
                    closable 
                    onClose={() => setSearchText('')}
                    style={{ marginRight: 0 }}
                  >
                    Search: "{searchText}"
                  </Tag>
                )}
                {(searchText || filterStatus !== 'a') && (
                  <Button 
                    type="link" 
                    size="small" 
                    onClick={clearFilters}
                    style={{ padding: 0 }}
                  >
                    Clear all
                  </Button>
                )}
              </Space>
            </Col>
          </Row>
        )}

        {/* Content Area */}
        {!screens.md ? (
          <Alert
            type="info"
            message="Mobile view can be implemented if required."
          />
        ) : (
          <>
            {isDataEmpty ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <Title level={4}>
                  {searchText || filterStatus !== 'a' ? 'No Matching Results Found' : 'No GA4 Connections Found'}
                </Title>
                <Paragraph type="secondary">
                  {searchText && `No connections match "${searchText}". `}
                  {filterStatus === 'b' && 'No healthy connections found. '}
                  {filterStatus === 'c' && 'No connections with issues found. '}
                  Try adjusting your filters.
                </Paragraph>
                {(searchText || filterStatus !== 'a') && (
                  <Button onClick={clearFilters} type="primary">
                    Clear All Filters
                  </Button>
                )}
              </div>
            ) : (
              <Table
                rowKey={record => record.user.id}
                columns={columns}
                dataSource={filteredData}
                pagination={{ 
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  size: 'small',
                }}
                size="small"
                scroll={{ x: screens.lg ? undefined : 1000 }}
              />
            )}
          </>
        )}
      </Space>
    </Card>
  );
};